import { Alert, Box, Button, CircularProgress, Container, IconButton, Typography, styled } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import palette from '../../../ui/palette';
import { ProofButton } from '../../../ui/button/ProofButton';
import { useUpload } from '.';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function DocUploadReview() {
  const { t } = useTranslation();
  const {
    files,
    addFiles,
    removeFile,
    docType,
    documentRequiredCount,
    isValidating,
    validationError,
    setValidationError,
    showValidationError,
    setShowValidationError,
    validationWarnings,
    fallbackTriggered,
    submit,
    isSubmitting,
    submitError,
    setSubmitError,
    goTo,
  } = useUpload();

  const addMoreFileInputRef = useRef<HTMLInputElement>(null);

  const warningByFile = useMemo(() => {
    const m = new Map<string, string>();
    validationWarnings.forEach(w => m.set(w.fileName, w.message));
    return m;
  }, [validationWarnings]);

  // Refresh-safe redirect: file blobs do not survive a hard refresh, so if we
  // land on review with no files, send the user back to /select.
  useEffect(() => {
    if (files.length === 0) goTo('select');
  }, [files.length, goTo]);

  if (files.length === 0) return null;

  return (
    <Container maxWidth="xs" disableGutters sx={{ py: { xs: 2, md: 3 } }}>
      <Typography variant="h2" textAlign="center" sx={{ fontWeight: 500, mb: 3 }}>
        {t('verification.type.document.documentsAdded')}
      </Typography>

      <Box sx={{ mb: 2, border: '1px solid', borderColor: palette.grey[20], borderRadius: 2, overflow: 'hidden' }}>
        {files.map((file, idx) => {
          const warning = warningByFile.get(file.name);
          return (
            <Box
              key={`item-${idx}`}
              sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: idx === files.length - 1 ? 'none' : '1px solid',
                borderColor: palette.grey[20],
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>
                  {file.name}
                </Typography>
                <Typography variant="body2" sx={{ color: palette.grey[60], whiteSpace: 'nowrap' }}>
                  {docType}
                </Typography>
                <IconButton size="small" onClick={() => removeFile(idx)} aria-label={t('verification.type.document.deleteButton') as string}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5, pl: 0.25 }}>
                {warning ? (
                  <>
                    <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main', mt: '2px' }} />
                    <Typography variant="caption" sx={{ color: 'warning.main', lineHeight: 1.4 }}>
                      {warning}
                    </Typography>
                  </>
                ) : (
                  <>
                    <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main', mt: '2px' }} />
                    <Typography variant="caption" sx={{ color: 'success.main' }}>
                      {t('verification.type.document.statusVerified')}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {isValidating && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: palette.primary[30] }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          <Typography sx={{ fontSize: '14px' }}>{t('verification.type.document.validatingDocument')}</Typography>
        </Box>
      )}

      {fallbackTriggered && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 13 }}>{t('verification.type.document.fallback.stepThreeWarningHint')}</Typography>
        </Alert>
      )}

      {validationError && showValidationError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => {
            setValidationError('');
            setShowValidationError(false);
          }}
        >
          <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>{validationError}</Typography>
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
          <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>{submitError}</Typography>
        </Alert>
      )}

      <Typography sx={{ mb: 2 }}>{t('verification.type.document.requiredDocumentsText', { count: documentRequiredCount, docType })}</Typography>

      <Button
        component="label"
        variant="contained"
        fullWidth
        disabled={isValidating}
        sx={{
          my: 2,
          textTransform: 'none',
          padding: '0.875rem 1.25rem',
          fontSize: '1rem',
          lineHeight: 1.5,
          fontWeight: 600,
          borderRadius: '8px',
          boxShadow: 'none',
          '&.MuiButton-contained': {
            backgroundColor: palette.primary[30],
            '&:hover': { backgroundColor: palette.primary[50] },
          },
          '&.Mui-disabled': { backgroundColor: palette.grey[30] },
        }}
      >
        {isValidating ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            {t('verification.type.document.validatingDocumentButton')}
          </>
        ) : (
          t('verification.type.document.addMoreDocuments')
        )}
        <VisuallyHiddenInput ref={addMoreFileInputRef} type="file" accept="application/pdf" onChange={e => addFiles(e.target.files, false)} multiple />
      </Button>

      <ProofButton variant="outlined" fullWidth disabled={files.length === 0 || isValidating || isSubmitting} onClick={submit}>
        {t('page.complete')}
      </ProofButton>
    </Container>
  );
}
